import cn from 'classnames';
import { FC, MouseEventHandler, ReactEventHandler, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import smileCat from '@/shared/assets/images/cats/smile.png';
import { useMedia } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import styles from './ReportCardLayout.module.scss';

type ReportCardLayoutProps = {
  isGenerationCompleted: boolean;
  previewAlt: string;
  title: ReactNode;
  children?: ReactNode;
  className?: string;
  date?: ReactNode;
  desktopActions?: ReactNode;
  isHidden?: boolean;
  isRemoving?: boolean;
  previewClassName?: string;
  previewSrc?: string;
  previewTestID?: string;
  shouldContainPreview?: boolean;
  shouldUseLightBackground?: boolean;
  testID?: string;
  dataComponent?: string;
  dataSection?: string;
  onAuxClick?: MouseEventHandler<HTMLDivElement>;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onImageError?: ReactEventHandler<HTMLImageElement>;
  onMobileActionsClick?: MouseEventHandler<HTMLButtonElement>;
};

export const ReportCardLayout: FC<ReportCardLayoutProps> = (props) => {
  const {
    isGenerationCompleted,
    previewAlt,
    title,
    children,
    className,
    date,
    desktopActions,
    isHidden,
    isRemoving,
    previewClassName,
    previewSrc,
    previewTestID,
    shouldContainPreview,
    shouldUseLightBackground,
    testID,
    dataComponent,
    dataSection,
    onAuxClick,
    onClick,
    onImageError,
    onMobileActionsClick,
  } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const hasPreview = Boolean(isGenerationCompleted && previewSrc);
  const backgroundColorStyle =
    hasPreview && !shouldUseLightBackground
      ? styles.blackBackground
      : styles.whiteBackground;
  const textColorStyle = hasPreview ? styles.textWhite : styles.textBlack;

  return (
    <div
      className={cn(
        styles.container,
        hasPreview && styles.generationCompleted,
        backgroundColorStyle,
        isRemoving && styles.removing,
        isHidden && styles.hiddenReport,
        className,
      )}
      data-testid={testID}
      data-component={dataComponent}
      data-section={dataSection}
      onAuxClick={onAuxClick}
      onClick={onClick}
    >
      {hasPreview && (
        <img
          className={cn(
            styles.previewImage,
            shouldContainPreview && styles.containedPreview,
            previewClassName,
          )}
          onError={onImageError}
          src={previewSrc}
          alt={previewAlt}
          data-testid={previewTestID}
        />
      )}

      <header className={cn(styles.header, textColorStyle)}>
        <h4
          className={cn(styles.title, textColorStyle, isMobile ? 'h2m' : 'h4')}
        >
          {title}

          {isMobile && onMobileActionsClick ? (
            <button
              type="button"
              className={styles.mobileInfoButton}
              onClick={onMobileActionsClick}
            >
              <Icon name="dots" size={32} className={styles.detailsIcon} />
            </button>
          ) : (
            desktopActions
          )}
        </h4>

        {date && (
          <p className={cn(textColorStyle, { p2m: isMobile, p2: !isMobile })}>
            {date}
          </p>
        )}
      </header>

      {isGenerationCompleted && !previewSrc && (
        <div className={styles.placeholder}>
          <img
            src={smileCat}
            className={styles.placeholderImage}
            alt={formatMessage({
              id: 'imgAltText.smileCat',
              defaultMessage: 'Smile cat',
            })}
          />

          <p
            className={cn(styles.placeholderText, {
              p2: !isMobile,
              p2m: isMobile,
            })}
          >
            <span>
              {formatMessage({
                id: 'reportCard.preview.generationReady',
                defaultMessage: 'Report is ready.',
              })}
            </span>

            <span>
              {formatMessage({
                id: 'reportCard.preview.generatingPreview',
                defaultMessage: 'Generating preview',
              })}
            </span>
          </p>
        </div>
      )}

      {children}
    </div>
  );
};
